/**
 * The "Adversários" campaign view: reach and sentiment across every tracked account, own
 * accounts first then adversaries by reach, matching `api`'s `reading/adversaries` contract.
 *
 * No Figma frame exists for this screen -- the owner asked for it directly in a QA crunch, with
 * no design pass. Built from existing components only (`SpecChart`, `StatCard`-free plain
 * `Table`, the shell's `Card`), following `../accounts/Account.tsx`'s own reading-screen shape
 * rather than a frame.
 */

import { useState, type ReactNode } from 'react';

import {
  cellText,
  SpecChart,
  type CategoryLabel,
  type ChartSpec,
} from '../../../chrome/chart/SpecChart';
import { Absent } from '../../../chrome/measure';
import { score } from '../../../chrome/measure/format';
import { QueryStates } from '../../../chrome/state/QueryStates';
import { getLocale } from '../../../i18n';
import { compact } from '../../../i18n/format';
import { Badge } from '../../../ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../../../ui/card';
import { Skeleton } from '../../../ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../ui/table';
import { CandidateAvatar } from '../accounts/account/CandidateSelect';
import { useTrailingWindow } from '../accounts/Account';
import type { ReadingWindow } from '../accounts/api/queries';
import { useAdversariesReading } from './api/queries';
import type { AdversariesReading, AdversaryAccountRow, CandidateGroup } from './api/schemas';
import { dm } from './messages';
import { WindowSelect, type WindowDays } from './WindowSelect';

const DEFAULT_WINDOW_DAYS: WindowDays = 7;

function accountDisplay(row: AdversaryAccountRow): string {
  return row.label ?? (row.handle ? `@${row.handle}` : dm('table.unknownHandle'));
}

function isEmptyReading(data: AdversariesReading): boolean {
  return !data.accounts.some((row) => row.alignment === 'adversary');
}

const category: CategoryLabel = (column, value) =>
  column === 'day' ? cellText(value) : String(value);

function ReachSentimentChart(props: { rows: AdversaryAccountRow[] }): ReactNode {
  // SpecChart names a series by its key, so the keys are the reader's labels.
  const reach = dm('table.reach');
  const sentiment = dm('table.sentiment');
  const spec: ChartSpec = {
    kind: 'bar',
    x: 'account',
    series: null,
    y: [reach],
    y2: [sentiment],
    units: {},
  };
  const rows = props.rows.map((row) => ({
    account: accountDisplay(row),
    [reach]: row.reach,
    [sentiment]: row.sentiment,
  }));
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg font-semibold">{dm('chart.reachSentiment.title')}</CardTitle>
      </CardHeader>
      <CardContent>
        <SpecChart
          spec={spec}
          rows={rows}
          category={category}
          ariaLabel={dm('chart.reachSentiment.aria')}
        />
      </CardContent>
    </Card>
  );
}

function ReachOverTimeChart(props: { rows: AdversaryAccountRow[] }): ReactNode {
  const spec: ChartSpec = {
    kind: 'line',
    x: 'day',
    series: 'account',
    y: ['value'],
    y2: [],
    units: {},
  };
  const rows = props.rows.flatMap((row) =>
    row.reach_days.map((point) => ({
      day: point.day,
      account: accountDisplay(row),
      value: point.value,
    })),
  );
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg font-semibold">{dm('chart.reachOverTime.title')}</CardTitle>
      </CardHeader>
      <CardContent>
        {rows.length === 0 ? (
          <p className="text-body-sm text-muted-foreground">{dm('empty.title')}</p>
        ) : (
          <SpecChart
            spec={spec}
            rows={rows}
            category={category}
            ariaLabel={dm('chart.reachOverTime.aria')}
          />
        )}
      </CardContent>
    </Card>
  );
}

function AccountRow(props: { row: AdversaryAccountRow; indent?: boolean }): ReactNode {
  const { row } = props;
  const locale = getLocale();
  return (
    <TableRow>
      <TableCell className={props.indent === true ? 'pl-8' : undefined}>
        {accountDisplay(row)}
      </TableCell>
      <TableCell>
        <Badge variant={row.alignment === 'own' ? 'secondary' : 'outline'}>
          {row.alignment === 'own' ? dm('table.alignment.own') : dm('table.alignment.adversary')}
        </Badge>
      </TableCell>
      <TableCell data-numeric>{compact(row.posts, locale)}</TableCell>
      <TableCell data-numeric>
        {row.reach !== null ? compact(row.reach, locale) : <Absent reason="not_measured" />}
      </TableCell>
      <TableCell data-numeric>
        {row.engagement !== null ? (
          compact(row.engagement, locale)
        ) : (
          <Absent reason="not_measured" />
        )}
      </TableCell>
      <TableCell data-numeric>{compact(row.targeting, locale)}</TableCell>
      <TableCell data-numeric>{compact(row.comments_read, locale)}</TableCell>
      <TableCell data-numeric>
        {row.sentiment !== null ? (
          score(row.sentiment * 100, locale)
        ) : (
          <Absent reason="not_measured" />
        )}
      </TableCell>
    </TableRow>
  );
}

function CandidateGroupRows(props: { group: CandidateGroup }): ReactNode {
  const { group } = props;
  const locale = getLocale();
  return (
    <>
      <TableRow className="bg-muted/40">
        <TableCell colSpan={2} className="font-medium">
          <div className="flex items-center gap-2">
            {group.candidate !== null && (
              <CandidateAvatar candidate={group.candidate} className="size-6" />
            )}
            {group.candidate?.name ?? dm('table.candidate.unassigned')}
          </div>
        </TableCell>
        <TableCell data-numeric className="font-medium">
          {compact(group.posts, locale)}
        </TableCell>
        <TableCell data-numeric className="font-medium">
          {group.reach !== null ? compact(group.reach, locale) : <Absent reason="not_measured" />}
        </TableCell>
        <TableCell data-numeric className="font-medium">
          {group.engagement !== null ? (
            compact(group.engagement, locale)
          ) : (
            <Absent reason="not_measured" />
          )}
        </TableCell>
        <TableCell data-numeric className="font-medium">
          {compact(group.targeting, locale)}
        </TableCell>
        <TableCell />
        <TableCell />
      </TableRow>
      {group.accounts.map((row) => (
        <AccountRow key={row.tracked_account_id} row={row} indent />
      ))}
    </>
  );
}

function AccountsTable(props: { own: AdversaryAccountRow[]; groups: CandidateGroup[] }): ReactNode {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg font-semibold">{dm('table.title')}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="overflow-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{dm('table.account')}</TableHead>
                <TableHead>{dm('table.alignment')}</TableHead>
                <TableHead>{dm('table.posts')}</TableHead>
                <TableHead>{dm('table.reach')}</TableHead>
                <TableHead>{dm('table.engagement')}</TableHead>
                <TableHead>{dm('table.targeting')}</TableHead>
                <TableHead>{dm('table.commentsRead')}</TableHead>
                <TableHead>{dm('table.sentiment')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {props.own.map((row) => (
                <AccountRow key={row.tracked_account_id} row={row} />
              ))}
              {props.groups.map((group) => (
                <CandidateGroupRows key={group.key} group={group} />
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}

function AdversariesEmpty(): ReactNode {
  return (
    <Card>
      <CardContent className="flex flex-col gap-1 py-4">
        <p className="text-body-sm font-medium">{dm('empty.title')}</p>
        <p className="text-body-sm text-muted-foreground">{dm('empty.description')}</p>
      </CardContent>
    </Card>
  );
}

function AdversariesSkeleton(): ReactNode {
  return (
    <div className="space-y-4">
      <Skeleton className="h-72 w-full" />
      <Skeleton className="h-72 w-full" />
      <Skeleton className="h-64 w-full" />
    </div>
  );
}

function AdversariesBody(props: { data: AdversariesReading }): ReactNode {
  return (
    <div className="space-y-4">
      <ReachSentimentChart rows={props.data.accounts} />
      <ReachOverTimeChart rows={props.data.accounts} />
      <AccountsTable
        own={props.data.accounts.filter((row) => row.alignment === 'own')}
        groups={props.data.adversary_groups}
      />
    </div>
  );
}

export function Adversaries(props: { campaignId: string }): ReactNode {
  const [days, setDays] = useState<WindowDays>(DEFAULT_WINDOW_DAYS);
  const window_: ReadingWindow = useTrailingWindow(days);
  const query = useAdversariesReading(props.campaignId, window_);

  return (
    <div className="space-y-6 w-full">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold tracking-tight sm:text-2xl">{dm('title')}</h1>
          <p className="text-body-sm text-muted-foreground">{dm('subtitle')}</p>
        </div>
        <WindowSelect value={days} onChange={setDays} />
      </div>
      <QueryStates
        query={query}
        isEmpty={isEmptyReading}
        loading={<AdversariesSkeleton />}
        empty={<AdversariesEmpty />}
      >
        {(data) => <AdversariesBody data={data} />}
      </QueryStates>
    </div>
  );
}
